import { AutocompleteInputChangeReason, AutocompleteProps } from '@mui/material'
import { useState } from 'react'

type AutocompletePropsAlias<T> = AutocompleteProps<AutocompleteNestedItem<T>, false, false, false>
export type AutocompleteNestedProps<T> = Pick<
    AutocompletePropsAlias<T>,
    | 'options'
    | 'getOptionLabel'
    | 'renderInput'
    | 'renderGroup'
    | 'renderOption'
    | 'fullWidth'
    | 'onChange'
    | 'noOptionsText'
    | 'defaultValue'
    | 'disableCloseOnSelect'
    | 'disabled'
> & {
    isFinalItem?: (item: T | undefined) => boolean // decides whether the drawer closes when a specific item is clicked
}

export interface AutocompleteNestedItem<T> {
    item: T
    group: string // nested group identifier, with individual nesting depths represented by their keys separated by /, ending on /
    key: string // unique key in group
    value: string // string representation of key
}

export const isRootParentItem = (option: AutocompleteNestedItem<any>) => option.group === '' && option.key === ''
const getGlobalKey = (option: AutocompleteNestedItem<unknown>) => `${option.group}${option.key}/`
const getParentItem = <T>(options: readonly AutocompleteNestedItem<T>[], previous: AutocompleteNestedItem<T> | null) =>
    options.find((option) => {
        return !isRootParentItem(option) && getGlobalKey(option) === previous?.group
    }) ?? null

export const useAutocompleteNested = <T>(props: AutocompleteNestedProps<T>): AutocompletePropsAlias<T> => {
    const {
        options,
        getOptionLabel,
        renderInput,
        renderGroup,
        renderOption,
        fullWidth,
        onChange,
        noOptionsText,
        defaultValue,
        disableCloseOnSelect,
        isFinalItem,
        disabled,
    } = props

    const [inputValue, setInputValue] = useState('')
    const [value, setValue] = useState<AutocompleteNestedItem<T> | null>(defaultValue ?? null)
    const [inputChangeReason, setInputChangeReason] = useState<AutocompleteInputChangeReason>('clear')
    const [open, setOpen] = useState(false)

    // sort passed options by the nesting depth and alphanumeric value
    const sortedOptions = [...options].sort((a, b) => {
        // sort parent items first always
        if (isRootParentItem(a)) return -1
        if (isRootParentItem(b)) return 1

        const difference = a.group.split('/').length - b.group.split('/').length

        // https://stackoverflow.com/questions/2140627/how-to-do-case-insensitive-string-comparison
        if (difference === 0) return a.value.localeCompare(b.value, undefined, { sensitivity: 'base' })
        else return difference
    })

    // TODO: Some kind of quick exit would be nice, but didnt find a way
    // tried with onkeydown, onchange and onselect, but none catch doubleclick. so we just have to esc for now
    // basic autocomplete props with automatic types
    return {
        // pass down props
        renderInput,
        renderGroup,
        renderOption,
        fullWidth,
        noOptionsText,
        disableCloseOnSelect,
        disabled,
        options: sortedOptions,
        getOptionLabel: getOptionLabel ?? getGlobalKey,
        groupBy: (option) => (isRootParentItem(option) ? '/' : option.group), // fix the grouping for empty "go back" options
        isOptionEqualToValue: (option, value) => getGlobalKey(option) === getGlobalKey(value),
        filterOptions: (options) => {
            const search = inputValue.toLocaleLowerCase()

            return options.filter((option) => {
                // something manually entered
                if (inputChangeReason === 'input' && search !== '') {
                    // filter option by key
                    const inKey = getGlobalKey(option).toLocaleLowerCase().includes(search)
                    // by value
                    const inValue = option.value.toLocaleLowerCase().includes(search)
                    return inKey || inValue
                }

                // nothing selected
                // nothing manually entered
                if (!value) return option.group === '/'

                // always show the "back" option, since we are not at root level
                const isParent = isRootParentItem(option)
                //const isSibling = option.group === value.group
                const isChild = option.group === getGlobalKey(value)
                return isParent || isChild
            })
        },
        inputValue,
        onInputChange: (_, value, reason) => {
            setInputValue(value)
            setInputChangeReason(reason)
        },
        value,
        onChange: (event, rawValue, reason, details) => {
            // hide root parent clicks as clicking the parent, or in the case of first level, null (aka clear)
            const newValue = rawValue && isRootParentItem(rawValue) ? getParentItem(options, value) : rawValue
            setValue(newValue)

            // pass down event
            if (onChange) onChange(event, newValue, reason, details)
            // check if the selected item is final, if so close the drawer
            if (isFinalItem && isFinalItem(newValue?.item)) setOpen(false)
        },
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
    }
}
